import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { LAYOUT_TYPE_STUDENT } from "constants/theme.constant";

const COOKIE_CONSENT_KEY = "tsv_cookie_consent";

const readConsent = () => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch (e) {
    return null;
  }
};

const saveConsent = () => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
  } catch (e) {
    // Storage blocked: the notice closes for this visit and shows again next time.
  }
};

// Room under the page for the card: its height plus this gap. The student layout already leaves
// room for the tab bar, so the card's offset above it isn't counted again.
const CARD_GAP = 24;

// The room is published as a CSS variable and taken inside each layout's content column by
// CookieNoticeSpace, so a page only grows when it's long enough to need it and the side
// navigation still runs to the bottom. (A spacer after the whole app made every page scroll
// past the end of its layout.)
const SPACE_VAR = "--cookie-notice-space";

/** Room at the end of a layout's content for the cookie card; none unless the card needs it. */
export const CookieNoticeSpace = () => (
  <div aria-hidden="true" data-testid="cookie-notice-space" className="shrink-0" style={{ height: `var(${SPACE_VAR}, 0px)` }} />
);

/**
 * A small card in the corner; on phones it sits above the student tab bar. While it shows, a
 * gap at the end of the page lets the last buttons scroll clear of it, and it stays under
 * drawers and dialogs (z-30) so it never covers one.
 */
const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [reserved, setReserved] = useState(0);
  const cardRef = useRef(null);
  const authenticated = useSelector((state) => Boolean(state.auth.session.token && state.auth.session.signedIn));
  const layoutType = useSelector((state) => state.theme.layout.type);
  const aboveTabBar = authenticated && layoutType === LAYOUT_TYPE_STUDENT;
  const { pathname } = useLocation();

  useEffect(() => {
    if (readConsent() !== "accepted") setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const measure = () => setReserved(cardRef.current ? cardRef.current.offsetHeight + CARD_GAP : 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [visible]);

  // After Accept the gap stays until the next page, so the page doesn't jump under the reader.
  useEffect(() => {
    if (!visible) setReserved(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a page change releases the gap
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement.style;
    if (reserved) root.setProperty(SPACE_VAR, `${reserved}px`);
    else root.removeProperty(SPACE_VAR);
    return () => root.removeProperty(SPACE_VAR);
  }, [reserved]);

  if (!visible) return null;

  const accept = () => {
    saveConsent();
    setVisible(false);
  };

  return (
    <section
      ref={cardRef}
      aria-label="Cookie notice"
      className={classNames(
        "fixed inset-x-3 z-[29] flex flex-wrap items-center gap-x-4 gap-y-3 p-4 rounded-card border border-line bg-white shadow-float dark:bg-gray-800 dark:border-gray-700",
        "md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[420px]",
        aboveTabBar ? "bottom-[calc(4.75rem+env(safe-area-inset-bottom))]" : "bottom-3"
      )}
    >
      <p className="m-0 flex-1 basis-60 text-[13px] text-ink-secondary dark:text-gray-300">
        We use cookies to improve your experience. See our{" "}
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-brand underline hover:text-brand-hover">
          privacy and cookies policy
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        .
      </p>
      <button type="button" onClick={accept} className="h-9 px-4 rounded-control bg-brand text-white font-medium hover:bg-brand-hover">
        Accept
      </button>
    </section>
  );
};

export default CookieConsentBanner;
